import React, { useState } from 'react';
import { supabase } from './supabaseClient.js';

const CRM_FIELDS = new Set(['company', 'contact_name', 'email', 'phone', 'work_type', 'site_description',
  'location', 'internal_notes', 'client_notes', 'last_contact', 'last_completed', 'last_completed_year',
  'invoice_number', 'booking_status', 'value', 'estimated_hours', 'area_acres']);
const NUMERIC_FIELDS = new Set(['value', 'estimated_hours', 'area_acres', 'chem_ru_l', 'chem_esp_ml',
  'chem_navius_g', 'chem_telar_g', 'chem_24d_l', 'chem_clearview_g', 'chem_overdrive_g', 'chem_trillion_l', 'last_completed_year']);

const CSV_COLUMN_MAP = {
  'company': 'company',
  'contact': 'contact_name',
  'contact_name': 'contact_name',
  'email': 'email',
  'phone': 'phone',
  'work_type': 'work_type',
  'type': 'work_type',
  'site_description': 'site_description',
  'site': 'site_description',
  'location': 'location',
  'address': 'location',
  'internal_notes': 'internal_notes',
  'notes': 'internal_notes',
  'last_contact': 'last_contact',
  'last_completed': 'last_completed',
  'invoice': 'invoice_number',
  'invoice_number': 'invoice_number',
  'booking_status': 'booking_status',
  'status': 'booking_status',
  'value': 'value',
  'price': 'value',
  'estimated_hours': 'estimated_hours',
  'hours': 'estimated_hours',
  'area_acres': 'area_acres',
  'acres': 'area_acres',
  'area': 'area_acres',
  'completed_by': 'completed_by',
  'crew': 'completed_by',
  'chem_ru_l': 'chem_ru_l',
  'ru': 'chem_ru_l',
  'chem_esp_ml': 'chem_esp_ml',
  'esp': 'chem_esp_ml',
  'chem_navius_g': 'chem_navius_g',
  'navius': 'chem_navius_g',
  'chem_telar_g': 'chem_telar_g',
  'telar': 'chem_telar_g',
  'chem_24d_l': 'chem_24d_l',
  '24d': 'chem_24d_l',
  '2,4-d': 'chem_24d_l',
  'chem_clearview_g': 'chem_clearview_g',
  'clearview': 'chem_clearview_g',
  'chem_overdrive_g': 'chem_overdrive_g',
  'overdrive': 'chem_overdrive_g',
  'chem_trillion_l': 'chem_trillion_l',
  'trillion': 'chem_trillion_l',
  'day': 'day_number',
  'day_number': 'day_number',
  'sort_order': 'sort_order',
  'order': 'sort_order',
  'client_notes': 'client_notes',
  'client notes': 'client_notes',
  'client notes 2026': 'client_notes',
  'oprec': 'oprec_link',
  'oprec_link': 'oprec_link',
  'oprec link': 'oprec_link',
  'quote': 'quote_link',
  'quote_link': 'quote_link',
  'quote link': 'quote_link',
  'kmz': 'kmz_link',
  'kmz_link': 'kmz_link',
  'kmz link': 'kmz_link',
  'image': 'image_link',
  'image_link': 'image_link',
  'image link': 'image_link',
  'last completed year': 'last_completed_year',
  'last_completed_year': 'last_completed_year',
  'amount': 'value',
  'amount ($)': 'value',
  'time (hrs)': 'estimated_hours',
  'area (acres)': 'area_acres',
  'phone number': 'phone',
  'contact name': 'contact_name',
  'contact email': 'email',
  'company name': 'company',
  'location(s)': 'location',
  'locations': 'location',
  'site or travel notes': 'internal_notes',
  'ru (l)': 'chem_ru_l',
  'esp (ml)': 'chem_esp_ml',
  'navius (g)': 'chem_navius_g',
  'telar (g)': 'chem_telar_g',
  '2,4-d (l)': 'chem_24d_l',
  'clearview (g)': 'chem_clearview_g',
  'overdrive (g)': 'chem_overdrive_g',
  'trillion (l)': 'chem_trillion_l',
};

const parseCsv = (text) => {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return { headers: [], rows: [] };

  // Simple CSV parse (handles quoted fields with commas)
  const parseLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map((line) => {
    const vals = parseLine(line);
    const obj = {};
    headers.forEach((h, i) => {
      obj[h] = vals[i] || '';
    });
    return obj;
  });

  return { headers, rows };
};

const CsvImportPage = () => {
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [routeId, setRouteId] = useState('');
  const [routes, setRoutes] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [importMode, setImportMode] = useState('unassigned'); // 'unassigned' or 'route'

  // Fetch routes for the dropdown
  React.useEffect(() => {
    supabase
      .from('routes')
      .select('id, route_name')
      .order('start_date', { ascending: false })
      .then(({ data }) => setRoutes(data || []));
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setCsvText(text);
      handleParse(text);
    };
    reader.readAsText(file);
  };

  const handleParse = (text) => {
    const { headers, rows } = parseCsv(text || csvText);
    setParsed({ headers, rows });
    setResult(null);

    // Auto-map headers
    const autoMap = {};
    headers.forEach((h) => {
      const normalized = h.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      if (CSV_COLUMN_MAP[normalized]) {
        autoMap[h] = CSV_COLUMN_MAP[normalized];
      }
    });
    setMapping(autoMap);
  };

  const handleImport = async () => {
    if (!parsed || parsed.rows.length === 0) return;
    setImporting(true);
    setResult(null);

    const targetRouteId = importMode === 'route' ? routeId || null : null;
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < parsed.rows.length; i++) {
      const row = parsed.rows[i];
      const crmData = { booking_status: 'not booked' };
      const routeData = {};

      // Apply column mapping, splitting into CRM vs route fields
      for (const [csvCol, dbCol] of Object.entries(mapping)) {
        const val = row[csvCol];
        if (!val) continue;

        let parsed_val;
        if (NUMERIC_FIELDS.has(dbCol)) {
          const num = parseFloat(val.replace(/[$,]/g, ''));
          if (!isNaN(num)) parsed_val = num;
        } else if (['day_number', 'sort_order'].includes(dbCol)) {
          const num = parseInt(val);
          if (!isNaN(num)) parsed_val = num;
        } else {
          parsed_val = val;
        }
        if (parsed_val === undefined) continue;

        if (CRM_FIELDS.has(dbCol)) {
          crmData[dbCol] = parsed_val;
        } else {
          routeData[dbCol] = parsed_val;
        }
      }

      // Insert CRM job first
      const { data: newCrmJob, error: crmErr } = await supabase
        .from('crm_jobs')
        .insert([crmData])
        .select('id')
        .single();

      if (crmErr) {
        console.error(`Row ${i + 1} CRM error:`, crmErr);
        errorCount++;
        continue;
      }

      // If importing to a route, create route_item linked to crm_job
      if (targetRouteId) {
        const { error: routeErr } = await supabase.from('route_items').insert([{
          ...routeData,
          crm_job_id: newCrmJob.id,
          row_type: 'JOB',
          route_id: targetRouteId,
          sort_order: i + 1,
        }]);
        if (routeErr) {
          console.error(`Row ${i + 1} route_item error:`, routeErr);
          errorCount++;
          continue;
        }
      }

      successCount++;
    }

    setResult({ successCount, errorCount, total: parsed.rows.length });
    setImporting(false);
  };

  const DB_COLUMNS = [
    '', 'company', 'contact_name', 'email', 'phone', 'work_type', 'site_description',
    'location', 'internal_notes', 'client_notes', 'last_contact', 'last_completed',
    'last_completed_year', 'invoice_number',
    'booking_status', 'value', 'estimated_hours', 'area_acres', 'completed_by',
    'oprec_link', 'quote_link',
    'day_number', 'sort_order',
    'chem_ru_l', 'chem_esp_ml', 'chem_navius_g', 'chem_telar_g',
    'chem_24d_l', 'chem_clearview_g', 'chem_overdrive_g', 'chem_trillion_l',
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Import CSV</h1>

      {/* Step 1: Upload */}
      <div className="bg-gray-700 rounded-lg p-4 mb-4">
        <h2 className="text-white font-semibold mb-2">1. Upload CSV File</h2>
        <input
          type="file"
          accept=".csv,.txt"
          onChange={handleFileUpload}
          className="text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-green-700 file:text-white hover:file:bg-green-600 file:cursor-pointer"
        />
        <p className="text-xs text-gray-400 mt-2">
          CSV should have a header row. Columns are auto-mapped by name (company, location, value, hours, etc.)
        </p>
      </div>

      {parsed && (
        <>
          {/* Step 2: Review mapping */}
          <div className="bg-gray-700 rounded-lg p-4 mb-4">
            <h2 className="text-white font-semibold mb-2">
              2. Column Mapping ({parsed.rows.length} rows found)
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {parsed.headers.map((h) => (
                <div key={h} className="flex items-center gap-2">
                  <span className="text-xs text-gray-300 w-28 truncate shrink-0" title={h}>{h}</span>
                  <span className="text-gray-400 text-xs">&rarr;</span>
                  <select
                    value={mapping[h] || ''}
                    onChange={(e) => setMapping({ ...mapping, [h]: e.target.value || undefined })}
                    className="flex-1 px-2 py-1 bg-gray-900 border border-gray-500 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-green-600"
                  >
                    {DB_COLUMNS.map((col) => (
                      <option key={col} value={col}>{col || '— skip —'}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Step 3: Preview */}
          <div className="bg-gray-700 rounded-lg p-4 mb-4 overflow-x-auto">
            <h2 className="text-white font-semibold mb-2">3. Preview (first 5 rows)</h2>
            <table className="w-full text-xs text-gray-300">
              <thead>
                <tr>
                  {parsed.headers.filter((h) => mapping[h]).map((h) => (
                    <th key={h} className="text-left px-2 py-1 text-gray-400 font-medium border-b border-gray-500/30">
                      {mapping[h]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsed.rows.slice(0, 5).map((row, i) => (
                  <tr key={i}>
                    {parsed.headers.filter((h) => mapping[h]).map((h) => (
                      <td key={h} className="px-2 py-1 truncate max-w-[150px]">{row[h]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Step 4: Import target */}
          <div className="bg-gray-700 rounded-lg p-4 mb-4">
            <h2 className="text-white font-semibold mb-2">4. Import Destination</h2>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="radio"
                  value="unassigned"
                  checked={importMode === 'unassigned'}
                  onChange={(e) => setImportMode(e.target.value)}
                  className="accent-green-600"
                />
                Unassigned pool (no route)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="radio"
                  value="route"
                  checked={importMode === 'route'}
                  onChange={(e) => setImportMode(e.target.value)}
                  className="accent-green-600"
                />
                Assign to route:
              </label>
              {importMode === 'route' && (
                <select
                  value={routeId}
                  onChange={(e) => setRouteId(e.target.value)}
                  className="px-3 py-1.5 bg-gray-900 border border-gray-500 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-green-600"
                >
                  <option value="">Select route...</option>
                  {routes.map((r) => (
                    <option key={r.id} value={r.id}>{r.route_name}</option>
                  ))}
                </select>
              )}
            </div>

            <button
              onClick={handleImport}
              disabled={importing || (importMode === 'route' && !routeId)}
              className="mt-4 px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-600 disabled:opacity-50 transition-colors"
            >
              {importing ? `Importing... (${parsed.rows.length} rows)` : `Import ${parsed.rows.length} Jobs`}
            </button>
          </div>

          {/* Result */}
          {result && (
            <div className={`rounded-lg p-4 mb-4 ${result.errorCount > 0 ? 'bg-amber-400/10 border border-amber-400/30' : 'bg-green-600/10 border border-green-600/30'}`}>
              <p className="text-sm text-white">
                Imported <strong>{result.successCount}</strong> of {result.total} jobs.
                {result.errorCount > 0 && (
                  <span className="text-amber-400"> {result.errorCount} failed (check console for details).</span>
                )}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CsvImportPage;
